/**
 * Value wrapper for type-safe coercion without typeof checks
 *
 * Usage:
 *   const sym = new SymbolValue('true');
 *   if (sym.isBool()) return sym.asBool();  // true
 *   return sym.asString();                   // 'true'
 */
/** Wrapper for symbol token values */
export class SymbolValue {
    raw;
    constructor(raw) {
        this.raw = raw;
    }
    /** Raw symbol string */
    get value() {
        return this.raw;
    }
    /** Check if this is a boolean literal (true/false/t/nil) */
    isBool() {
        return this.raw === 'true' || this.raw === 'false' || this.raw === 't' || this.raw === 'nil';
    }
    /** Convert to boolean - throws if not a bool literal */
    asBool() {
        if (this.raw === 'true' || this.raw === 't')
            return true;
        if (this.raw === 'false' || this.raw === 'nil')
            return false;
        throw new Error(`Symbol '${this.raw}' is not a boolean literal`);
    }
    /** Try to convert to boolean - returns undefined if not a bool literal */
    tryBool() {
        if (this.raw === 'true' || this.raw === 't')
            return true;
        if (this.raw === 'false' || this.raw === 'nil')
            return false;
        return undefined;
    }
    /** Convert to integer - throws if not a valid integer */
    asInt() {
        const n = Number.parseInt(this.raw, 10);
        if (Number.isNaN(n))
            throw new Error(`Symbol '${this.raw}' is not an integer`);
        return n;
    }
    /** Try to convert to integer - returns undefined if not valid */
    tryInt() {
        const n = Number.parseInt(this.raw, 10);
        return Number.isNaN(n) ? undefined : n;
    }
    /** Convert to float - throws if not a valid number */
    asFloat() {
        const n = Number.parseFloat(this.raw);
        if (Number.isNaN(n))
            throw new Error(`Symbol '${this.raw}' is not a number`);
        return n;
    }
    /** Try to convert to float - returns undefined if not valid */
    tryFloat() {
        const n = Number.parseFloat(this.raw);
        return Number.isNaN(n) ? undefined : n;
    }
    /** Get as string (identity) */
    asString() {
        return this.raw;
    }
    toString() {
        return this.raw;
    }
}
/** Wrapper for string token values */
export class StringValue {
    raw;
    constructor(raw) {
        this.raw = raw;
    }
    /** Raw string content */
    get value() {
        return this.raw;
    }
    /** Check if this looks like a boolean string */
    isBool() {
        const lower = this.raw.toLowerCase();
        return lower === 'true' || lower === 'false';
    }
    /** Convert to boolean - throws if not a bool string */
    asBool() {
        const lower = this.raw.toLowerCase();
        if (lower === 'true')
            return true;
        if (lower === 'false')
            return false;
        throw new Error(`String '${this.raw}' is not a boolean`);
    }
    /** Try to convert to boolean */
    tryBool() {
        const lower = this.raw.toLowerCase();
        if (lower === 'true')
            return true;
        if (lower === 'false')
            return false;
        return undefined;
    }
    /** Convert to integer */
    asInt() {
        const n = Number.parseInt(this.raw, 10);
        if (Number.isNaN(n))
            throw new Error(`String '${this.raw}' is not an integer`);
        return n;
    }
    /** Try to convert to integer */
    tryInt() {
        const n = Number.parseInt(this.raw, 10);
        return Number.isNaN(n) ? undefined : n;
    }
    /** Convert to float */
    asFloat() {
        const n = Number.parseFloat(this.raw);
        if (Number.isNaN(n))
            throw new Error(`String '${this.raw}' is not a number`);
        return n;
    }
    /** Try to convert to float */
    tryFloat() {
        const n = Number.parseFloat(this.raw);
        return Number.isNaN(n) ? undefined : n;
    }
    /** Get as string (identity) */
    asString() {
        return this.raw;
    }
    /** Check if empty */
    isEmpty() {
        return this.raw.length === 0;
    }
    /** Check if blank (empty or whitespace only) */
    isBlank() {
        return this.raw.trim().length === 0;
    }
    toString() {
        return this.raw;
    }
}
/** Wrapper for number token values */
export class NumberValue {
    num;
    constructor(raw) {
        this.num = typeof raw === 'number' ? raw : Number.parseFloat(raw);
    }
    /** Raw number value */
    get value() {
        return this.num;
    }
    /** Always false for numbers */
    isBool() {
        return false;
    }
    /** Convert to boolean (0 = false, else true) */
    asBool() {
        return this.num !== 0;
    }
    /** Convert to integer (truncate) */
    asInt() {
        return Math.trunc(this.num);
    }
    /** Get as float (identity) */
    asFloat() {
        return this.num;
    }
    /** Convert to string */
    asString() {
        return String(this.num);
    }
    /** Check if integer (no decimal part) */
    isInt() {
        return Number.isInteger(this.num);
    }
    toString() {
        return String(this.num);
    }
}
//# sourceMappingURL=value.js.map